import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { BatchRecipient, BatchStatusCounts } from '@ghostscreen/contracts';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { formatNumber } from '../../i18n';
import { api } from '../../lib/api';

const POLL_MS = 3000;

// duplicate_active_command reads "queued", not "running": the existing command
// is still pending, so nothing has started on the player yet (review feedback).
const EXCLUSION_REASONS = [
  'no_paired_device',
  'device_revoked',
  'command_not_supported',
  'duplicate_active_command',
] as const;

const COMMAND_STATUSES = ['pending', 'sent', 'acknowledged', 'executed', 'failed', 'expired'] as const;

type GroupsT = TFunction<'groups'>;

/**
 * Live progress for one batch.
 *
 * Deliberately never renders a single green "complete" state. A batch that
 * finished with failures, expiries or exclusions says so in the headline,
 * because collapsing partial failure into success is exactly how an
 * operator ends up believing screens were updated when they were not.
 */
export function BatchProgress({
  organisationId,
  batchId,
}: {
  organisationId: string;
  batchId: string;
}) {
  const { t } = useTranslation('groups');

  const batchQuery = useQuery({
    queryKey: ['device-command-batch', organisationId, batchId],
    queryFn: () => api.getDeviceCommandBatch(organisationId, batchId),
    // Stop polling once nothing can change again.
    refetchInterval: (query) => (isSettled(query.state.data?.counts) ? false : POLL_MS),
  });

  const recipientsQuery = useQuery({
    queryKey: ['device-command-batch-recipients', organisationId, batchId],
    queryFn: () => api.listBatchRecipients(organisationId, batchId, { limit: 200 }),
    refetchInterval: (query) =>
      isSettled(batchQuery.data?.counts) || query.state.data === undefined ? false : POLL_MS,
  });

  if (batchQuery.isPending) return <QueryLoadingBlock />;
  if (batchQuery.isError || !batchQuery.data) {
    return (
      <QueryErrorState
        message={t('batch.loadError')}
        onRetry={() => void batchQuery.refetch()}
      />
    );
  }

  const batch = batchQuery.data;
  const counts = batch.counts;

  return (
    <section className="space-y-3 rounded-xl border border-line bg-panel p-4">
      <header className="space-y-1">
        <h3 className="font-medium">
          {batch.commandType} — {batch.screenGroupName}
        </h3>
        <p className="text-sm text-muted">{headline(counts, t)}</p>
      </header>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <Count label={t('batch.counts.targeted')} value={counts.total} />
        <Count label={t('batch.counts.waiting')} value={counts.pending} />
        <Count label={t('batch.counts.delivered')} value={counts.sent} />
        <Count label={t('batch.counts.acknowledged')} value={counts.acknowledged} />
        <Count label={t('batch.counts.done')} value={counts.executed} />
        <Count label={t('batch.counts.failed')} value={counts.failed} tone="bad" />
        <Count label={t('batch.counts.expired')} value={counts.expired} tone="bad" />
        <Count label={t('batch.counts.skipped')} value={counts.excluded} tone="warn" />
      </dl>

      {recipientsQuery.data ? (
        <RecipientTable items={recipientsQuery.data.items} />
      ) : recipientsQuery.isPending ? (
        <QueryLoadingBlock variant="rows" />
      ) : null}
    </section>
  );
}

/** Terminal states only: nothing further can change once no command is in flight. */
function isSettled(counts: BatchStatusCounts | undefined): boolean {
  if (!counts) return false;
  return counts.pending === 0 && counts.sent === 0 && counts.acknowledged === 0;
}

/**
 * One honest sentence. Full success is only claimed when every targeted
 * screen actually executed and none were skipped.
 */
function headline(counts: BatchStatusCounts, t: GroupsT): string {
  const settled = isSettled(counts);
  const problems = counts.failed + counts.expired + counts.excluded;

  if (!settled) {
    return t('batch.inProgress', { executed: counts.executed, total: counts.total });
  }
  if (problems === 0) {
    return t('batch.allDone', { count: counts.total });
  }
  return t('batch.finishedWithIssues', {
    executed: counts.executed,
    total: counts.total,
    failed: counts.failed,
    expired: counts.expired,
    excluded: counts.excluded,
  });
}

function Count({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'bad' | 'warn';
}) {
  const emphasise = value > 0 && tone !== 'default';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd
        className={
          emphasise
            ? tone === 'bad'
              ? 'font-semibold text-red-600'
              : 'font-semibold text-amber-600'
            : ''
        }
      >
        {formatNumber(value)}
      </dd>
    </div>
  );
}

/** Per-screen outcomes, so an aggregate can always be drilled into. */
function RecipientTable({ items }: { items: BatchRecipient[] }) {
  const { t } = useTranslation('groups');
  if (items.length === 0) {
    return <p className="text-sm text-muted">{t('batch.noRecipients')}</p>;
  }

  return (
    <div className="overflow-x-auto">
    <table className="w-full min-w-[420px] text-start text-sm">
      <thead>
        <tr className="text-xs uppercase tracking-wide text-muted">
          <th className="py-1">{t('batch.columns.screen')}</th>
          <th className="py-1">{t('batch.columns.outcome')}</th>
          <th className="py-1">{t('batch.columns.detail')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="border-t border-line">
            <td className="py-1">
              {item.screenName ?? item.deviceCode ?? t('batch.removedScreen')}
            </td>
            <td className="py-1">{outcomeLabel(item, t)}</td>
            <td className="py-1 text-muted">{item.resultMessage ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function outcomeLabel(item: BatchRecipient, t: GroupsT): string {
  const reason = EXCLUSION_REASONS.find((known) => known === item.excludedReason);
  if (reason) return t(`batch.exclusions.${reason}`);
  if (item.excludedReason) return t('batch.counts.skipped');
  if (!item.commandStatus) return t('batch.unknown');
  const status = COMMAND_STATUSES.find((known) => known === item.commandStatus);
  return status ? t(`batch.statuses.${status}`) : item.commandStatus;
}
